import { __, sprintf } from '@wordpress/i18n';

/* ---- "N in cart" button labels ----
   Mirrors WooCommerce's blockified product button: each card CTA reflects how
   many of its product/variation the cart holds, and label changes roll — old
   text slides out the top, new text slides in from below. Counts come from a
   Store API cart read; adds/removals are observed via Woo's jQuery events
   (the same ones the mini cart listens to). */

let cartCounts: Record<string, number> = {};
let cartLoaded = false;

const cardButtons = (): HTMLElement[] =>
    Array.from(document.querySelectorAll<HTMLElement>('.stgb-pc-button.ajax_add_to_cart[data-product_id]'));

// Store API cart items carry the VARIATION id as item.id for variations, so
// one id → quantity map covers simple and swatch (variation) buttons alike.
const targetLabel = (btn: HTMLElement): string => {
    const id = btn.getAttribute('data-product_id') || '';
    const count = cartCounts[id] || 0;
    const original = btn.getAttribute('data-stgb-add-label') || '';
    return count > 0
        ? sprintf(
            /* translators: %d: how many of this product are in the cart */
            __('%d in cart', 'stgb'),
            count
        )
        : original;
};

const ROLL_OUT_MS = 160;

const setLabel = (btn: HTMLElement, animate: boolean) => {
    const label = btn.querySelector<HTMLElement>('.stgb-pc-button-label');
    if (!label) return;
    // First touch: remember the card's authored CTA text to fall back to
    // whenever the product leaves the cart again.
    if (!btn.getAttribute('data-stgb-add-label')) {
        btn.setAttribute('data-stgb-add-label', label.textContent || '');
    }
    const next = targetLabel(btn);
    if ((label.textContent || '') === next) return;
    if (!animate) {
        label.textContent = next;
        return;
    }
    label.classList.add('is-rolling-out');
    window.setTimeout(() => {
        label.textContent = next;
        label.classList.remove('is-rolling-out');
        label.classList.add('is-rolling-in');
        window.setTimeout(() => label.classList.remove('is-rolling-in'), 240);
    }, ROLL_OUT_MS);
};

const syncButtons = (animate: boolean) => {
    if (!cartLoaded) return;
    cardButtons().forEach((btn) => setLabel(btn, animate));
};

const refreshCartCounts = async (animate: boolean) => {
    try {
        // enqueue-assets.php localises app.siteUrl on this bundle.
        const w = window as any;
        const site = w.app && w.app.siteUrl ? String(w.app.siteUrl).replace(/\/$/, '') : '';
        const response = await fetch(`${site}/wp-json/wc/store/v1/cart`, { credentials: 'same-origin' });
        if (!response.ok) return;
        const data = await response.json();
        const counts: Record<string, number> = {};
        (Array.isArray(data.items) ? data.items : []).forEach((item: any) => {
            const id = String(item.id || '');
            if (id) counts[id] = (counts[id] || 0) + (Number(item.quantity) || 0);
        });
        cartCounts = counts;
        cartLoaded = true;
        syncButtons(animate);
    } catch {
        /* labels keep their current text; the next cart event retries */
    }
};

/* ---- Swatch → variation wiring ----
   Clicking a finish dot marks it selected and repoints the card's AJAX
   add-to-cart button at that variation; WooCommerce's own wc-add-to-cart
   handler does the rest (WC_AJAX::add_to_cart resolves a variation id posted
   as product_id). Delegated on the document so it also catches the suppressor
   selector's template-cloned cards. */
const onSwatchClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const swatch = target.closest('.stgb-pc-swatch');
    if (!(swatch instanceof HTMLButtonElement)) return;
    const card = swatch.closest('.stgb-pc-card');
    const variationId = swatch.getAttribute('data-variation-id');
    if (!card || !variationId) return;

    card.querySelectorAll<HTMLButtonElement>('.stgb-pc-swatch').forEach((other) => {
        const isThis = other === swatch;
        other.classList.toggle('is-selected', isThis);
        other.setAttribute('aria-pressed', isThis ? 'true' : 'false');
    });

    // Swap the card image to the selected variation's. src/srcset/sizes move
    // together — a stale srcset would beat the new src. Variations without
    // their own image carry the parent featured image in these attributes
    // (server-side fallback), so switching to them restores the original.
    const img = card.querySelector<HTMLImageElement>('.stgb-pc-media img');
    const imageSrc = swatch.getAttribute('data-image-src');
    if (img && imageSrc) {
        img.setAttribute('src', imageSrc);
        const srcset = swatch.getAttribute('data-image-srcset');
        const sizes = swatch.getAttribute('data-image-sizes');
        const alt = swatch.getAttribute('data-image-alt');
        if (srcset) img.setAttribute('srcset', srcset);
        else img.removeAttribute('srcset');
        if (sizes) img.setAttribute('sizes', sizes);
        else img.removeAttribute('sizes');
        if (alt) img.setAttribute('alt', alt);
    }

    const button = card.querySelector<HTMLElement>('.stgb-pc-button.ajax_add_to_cart');
    if (!button) return;
    button.setAttribute('data-product_id', variationId);
    // wc-add-to-cart.js reads $button.data(), and jQuery caches data after
    // first read — update the cache too or a second add after switching
    // swatches would post the previously cached variation.
    const w = window as any;
    if (w.jQuery) w.jQuery(button).data('product_id', variationId);
    // The label follows the newly selected variation's in-cart count.
    if (cartLoaded) setLabel(button, true);
};

// The bundle is also enqueued in the editor; card previews there must not
// become interactive.
if (!document.body.classList.contains('wp-admin')) {
    document.addEventListener('click', onSwatchClick);

    const boot = () => {
        // Only pages that actually render the shared cards pay for a cart read.
        if (!document.querySelector('.stgb-pc-button-label')) return;
        void refreshCartCounts(false);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    // Adds/removals (any source: card buttons, add-all, mini cart) → re-read
    // the cart and roll the affected labels.
    const w = window as any;
    if (w.jQuery) {
        w.jQuery(document.body).on('added_to_cart removed_from_cart', () => {
            void refreshCartCounts(true);
        });
        // Fragment restore on load — sync without the roll.
        w.jQuery(document.body).on('wc_fragments_refreshed wc_fragments_loaded', () => {
            void refreshCartCounts(false);
        });
    }

    // Fired by the suppressor selector when it clones fresh cards into its
    // results — stamp them from the already-loaded counts, no roll.
    document.addEventListener('stgb:cards-updated', () => syncButtons(false));
}

// Module scope (see the other blocks' react.ts files).
export {};
